import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Button } from '../ui/button';
import { History, Loader2, Users } from 'lucide-react';
import {
  ZPRO_PAGE,
  ZproMensagem,
  ZproMensagemBolha,
  ZproSeparadorDia,
  ZproTicket,
  chaveDia,
  erroMsg,
  fmt,
  statusLabel,
} from './zproHistorico';
import { useZproHabilitado } from './useZproHabilitado';

interface Bloco {
  ticket: ZproTicket;
  mensagens: ZproMensagem[];
  temMais: boolean;
  carregando: boolean;
}

interface Props {
  contatoId?: string | null;
  idZpro?: string | null;
}

/**
 * Histórico do Zpro na própria lista de mensagens do atendimento: aparece no topo da conversa,
 * acima das mensagens atuais, do atendimento mais antigo para o mais recente.
 */
export const HistoricoZproInline: React.FC<Props> = ({ contatoId, idZpro }) => {
  const habilitado = useZproHabilitado();
  const [tickets, setTickets] = useState<ZproTicket[] | null>(null);
  const [blocos, setBlocos] = useState<Bloco[]>([]); // do mais recente para o mais antigo (ordem de carga)
  const [carregandoLista, setCarregandoLista] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Trocou de contato/conversa: volta ao estado inicial
  useEffect(() => {
    setTickets(null);
    setBlocos([]);
    setErro(null);
    setCarregandoLista(false);
  }, [contatoId]);

  if (!habilitado || !contatoId || !idZpro) return null;

  const carregarMensagens = async (ticket: ZproTicket, offset: number) => {
    const atualiza = (fn: (b: Bloco) => Bloco) =>
      setBlocos((prev) => prev.map((b) => (b.ticket.id === ticket.id ? fn(b) : b)));
    try {
      const r = await api.get(`/contatos/${contatoId}/historico-zpro/tickets/${ticket.id}/mensagens`, {
        params: { limit: ZPRO_PAGE, offset },
      });
      const d = r?.data?.data;
      atualiza((b) => ({
        ...b,
        mensagens: offset === 0 ? d.mensagens : [...b.mensagens, ...d.mensagens],
        temMais: !!d.temMais,
        carregando: false,
      }));
    } catch (e) {
      setErro(erroMsg(e));
      atualiza((b) => ({ ...b, carregando: false }));
    }
  };

  const abrirTicket = (ticket: ZproTicket) => {
    setBlocos((prev) => [...prev, { ticket, mensagens: [], temMais: false, carregando: true }]);
    carregarMensagens(ticket, 0);
  };

  const iniciar = async () => {
    try {
      setCarregandoLista(true);
      setErro(null);
      const r = await api.get(`/contatos/${contatoId}/historico-zpro`);
      const lista: ZproTicket[] = r?.data?.data || [];
      setTickets(lista);
      if (lista[0]) abrirTicket(lista[0]);
    } catch (e) {
      setErro(erroMsg(e));
    } finally {
      setCarregandoLista(false);
    }
  };

  // Nada carregado ainda: apenas uma barra discreta no topo da conversa
  if (!tickets) {
    return (
      <div className="flex flex-col items-center gap-1 pb-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={iniciar}
          disabled={carregandoLista}
          className="bg-white/80 dark:bg-slate-700 dark:text-white text-xs h-7"
        >
          {carregandoLista ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <History className="h-3 w-3 mr-1" />}
          Ver histórico do Zpro
        </Button>
        {erro && <p className="text-xs text-red-600 dark:text-red-400">{erro}</p>}
      </div>
    );
  }

  const restantes = tickets.length - blocos.length;
  const proximo = tickets[blocos.length];
  const ordenados = [...blocos].reverse(); // mais antigo em cima, mais recente logo acima da conversa atual

  return (
    <div className="flex flex-col gap-1 pb-3 border-b border-dashed border-gray-400/60 mb-3">
      <div className="flex flex-col items-center gap-1 pb-1">
        <span className="inline-flex items-center gap-1 text-[11px] rounded-full px-3 py-0.5 bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-100">
          <History className="h-3 w-3" /> Histórico do Zpro · {tickets.length} atendimento(s)
        </span>
        {restantes > 0 && proximo && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => abrirTicket(proximo)}
            className="bg-white/80 dark:bg-slate-700 dark:text-white text-xs h-7"
          >
            Carregar atendimento anterior ({restantes} restante{restantes > 1 ? 's' : ''})
          </Button>
        )}
        {tickets.length === 0 && <p className="text-xs text-gray-500">Nenhum atendimento encontrado no Zpro.</p>}
        {erro && <p className="text-xs text-red-600 dark:text-red-400">{erro}</p>}
      </div>

      {ordenados.map((b) => (
        <div key={b.ticket.id} className="flex flex-col gap-1">
          <div className="flex justify-center pt-2">
            <span className="inline-flex items-center gap-1 text-[11px] rounded-md px-3 py-1 bg-violet-50 text-violet-900 border border-violet-200 dark:bg-slate-800 dark:text-violet-100 dark:border-violet-800 text-center">
              {b.ticket.grupo && <Users className="h-3 w-3" />}
              Zpro · {b.ticket.protocolo ? `Protocolo ${b.ticket.protocolo}` : `Ticket #${b.ticket.id}`} ·{' '}
              {fmt(b.ticket.criadoEm)}
              {b.ticket.fechadoEm ? ` → ${fmt(b.ticket.fechadoEm)}` : ''}
              {b.ticket.atendente ? ` · ${b.ticket.atendente}` : ''}
              {b.ticket.fila ? ` · ${b.ticket.fila}` : ''} · {statusLabel[b.ticket.status] || b.ticket.status}
            </span>
          </div>

          {b.carregando && (
            <p className="flex justify-center items-center gap-2 text-xs text-gray-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Carregando mensagens...
            </p>
          )}

          {!b.carregando && b.mensagens.length === 0 && (
            <p className="text-center text-xs text-gray-500">Sem mensagens neste atendimento.</p>
          )}

          {b.mensagens.map((m, i) => (
            <React.Fragment key={m.id}>
              {(i === 0 || chaveDia(b.mensagens[i - 1].criadaEm) !== chaveDia(m.criadaEm)) && (
                <ZproSeparadorDia iso={m.criadaEm} />
              )}
              <ZproMensagemBolha m={m} />
            </React.Fragment>
          ))}

          {b.temMais && !b.carregando && (
            <div className="text-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setBlocos((prev) => prev.map((x) => (x.ticket.id === b.ticket.id ? { ...x, carregando: true } : x)));
                  carregarMensagens(b.ticket, b.mensagens.length);
                }}
                className="bg-white/80 dark:bg-slate-700 dark:text-white text-xs h-7"
              >
                Carregar mais mensagens deste atendimento
              </Button>
            </div>
          )}
        </div>
      ))}

      <div className="flex justify-center pt-3">
        <span className="text-[11px] text-gray-600 dark:text-gray-300">— Conversa atual —</span>
      </div>
    </div>
  );
};

export default HistoricoZproInline;
